import React, { useState, useMemo } from "react";
import classNames from "classnames";
import { DAYS, ORDERS, formatRubles, pluralize } from "@/utils/helpers";
import {
  addDays,
  formatDayMonth,
  formatApiDate,
  getPresetRange,
  startOfDay,
} from "@/components/PeriodSelector/periodDates";
import { DashboardReferralI, DashboardReferralDriver } from "@/types/referals";
import { ReferralDetails } from "@/components/ReferralDetails/ReferralDetails";
import { getDashboardReferalDriver } from "@/api/base";
import { useStatisticsPeriodStore } from "@/store/statisticsPeriodStore";
import { useUserInfoStore } from "@/store/userInfoStore";
import { GoalRail, REFERRAL_WINDOW_DAYS, isWindowExpired } from "./GoalRail";
import cls from "./styles.module.scss";

interface FunnelReferralRowProps {
  referral: DashboardReferralI;
}

// TODO: заменить на дату первого реферала из API статистики
const ALL_TIME_START = new Date(2024, 0, 1);

export const FunnelReferralRow = ({ referral }: FunnelReferralRowProps) => {
  const yandexId = useUserInfoStore((s) => s.yandexId);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const isExpired = isWindowExpired(referral);

  const preset = useStatisticsPeriodStore((s) => s.preset);
  const customRange = useStatisticsPeriodStore((s) => s.customRange);
  const mode = useStatisticsPeriodStore((s) => s.mode);

  const today = useMemo(() => startOfDay(new Date()), []);

  // свой период из календаря важнее пресета; «Всё время» без даты
  // первого реферала отдаёт null — берём заведомо ранний старт
  const range = useMemo(
    () =>
      customRange ??
      getPresetRange(preset, today) ?? { from: ALL_TIME_START, to: today },
    [customRange, preset, today]
  );

  const [driverInfo, setDriverInfo] = useState<DashboardReferralDriver | null>(
    null
  );
  const [isDriverLoading, setIsDriverLoading] = useState<boolean>(false);

  const ordersText = `${referral.orders_in_window} ${pluralize(
    referral.orders_in_window,
    ORDERS
  )}`;

  let footer: React.ReactNode;
  if (isExpired) {
    const closedAt = addDays(
      new Date(referral.registered_at),
      REFERRAL_WINDOW_DAYS
    );
    footer = (
      <>
        <span className={cls.footLabel}>
          {ordersText} · 28 дней прошли {formatDayMonth(closedAt)}
        </span>
        <span className={cls.footDead}>целей не будет</span>
      </>
    );
  } else if (referral.orders_30.completed) {
    footer = (
      <>
        <span className={cls.footLabel}>{ordersText} · цели закрыты</span>
        <span className={cls.footDone}>выплачено</span>
      </>
    );
  } else {
    footer = (
      <>
        <span className={cls.footLabel}>
          {ordersText} · осталось {referral.days_remaining}{" "}
          {pluralize(referral.days_remaining, DAYS)}
        </span>
        <span className={cls.footPotential}>
          ещё {formatRubles(referral.potential.known_amount)}
        </span>
      </>
    );
  }

  const loadDriverInfo = async () => {
    setIsDriverLoading(true);

    try {
      const res = await getDashboardReferalDriver(
        referral.id,
        yandexId,
        formatApiDate(range.from),
        formatApiDate(range.to),
        mode
      );
      setDriverInfo(res);
    } catch (error) {
      console.log(error);
      setIsDetailsOpen(false);
    } finally {
      setIsDriverLoading(false);
    }
  };

  const openModal = () => {
    setIsDetailsOpen(true);
    loadDriverInfo();
  };

  const closeModal = () => {
    setIsDetailsOpen(false);
    setDriverInfo(null);
  };

  return (
    <>
      <button
        type="button"
        data-trigger="ref.funnel.referral"
        className={classNames(cls.referral, isExpired && cls.referralDead)}
        onClick={openModal}
      >
        <div className={cls.referralTop}>
          <div>
            <div
              className={classNames(
                cls.referralName,
                isExpired && cls.referralNameGrey
              )}
            >
              {referral.name}
            </div>
            <div className={cls.referralMeta}>
              {referral.service_display} · {referral.tariff_display} · с{" "}
              {formatDayMonth(new Date(referral.registered_at))}
            </div>
          </div>
          <div
            className={classNames(
              cls.referralEarned,
              !referral.total_accrued && cls.referralEarnedZero
            )}
          >
            {formatRubles(referral.total_accrued)}
          </div>
        </div>

        {referral.orders_in_window > 0 && (
          <div className={cls.referralRail}>
            <GoalRail referral={referral} />
          </div>
        )}

        <div className={cls.referralFoot}>{footer}</div>
      </button>

      <ReferralDetails
        driver={driverInfo}
        isLoading={isDriverLoading}
        isOpen={isDetailsOpen}
        onClose={closeModal}
      />
    </>
  );
};
